import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabase';
import { Plus, Trash2, Phone, Mail, AlertCircle, X } from 'lucide-react';
import { DataGrid } from './ui/DataGrid';
import { TextCell, SelectCell, SaveState } from './ui/EditableCell';
import { useRowAutosave } from './ui/useRowAutosave';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { MetricCard } from './ui/arc/metric-card';
import { ChipGroup } from './ui/arc/chip-group';

// Proveedores — grilla editable con autoguardado. Misma receta que Insumos
// (src/Insumos.tsx): root `tw`, encabezado, resumen, DataGrid con chips de filtro.

export type Provider = {
    id: number;
    name: string;
    whatsapp: string;
    email: string;
    order_lang?: 'es' | 'en' | null;
    created_at: string;
};

type Flag = 'sin_whatsapp' | 'sin_insumos';

export function Providers() {
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState<string | null>(null);
    const [fFlag, setFFlag] = useState<Flag | null>(null);
    // Cuántos insumos tiene cada proveedor: un proveedor en 0 es el que todavía no
    // sirve para armar un pedido, y conviene verlo sin salir de la pantalla.
    const [matCount, setMatCount] = useState<Record<number, number>>({});
    const { edit, editNow, state } = useRowAutosave<Provider>('providers', setProviders, {
        onError: m => setMsg('No se pudo guardar: ' + m),
    });

    useEffect(() => {
        fetchProviders();
    }, []);

    // Crear = insertar una fila vacía y editarla en la grilla. Un formulario aparte
    // para tres campos que ya son editables inline no agrega nada.
    const createProvider = async () => {
        const { data, error } = await supabase.from('providers')
            .insert({ name: '', whatsapp: '', email: '' }).select().single();
        if (error) { setMsg('No se pudo crear: ' + error.message); return; }
        setProviders(ps => [data as Provider, ...ps]);
        setFFlag(null);
    };

    const fetchProviders = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('providers')
                .select('*')
                .order('name', { ascending: true });

            if (error) throw error;
            setProviders(data || []);
            // conteo de insumos por proveedor (directo o vía el grupo padre)
            const [mats, grs] = await Promise.all([
                supabase.from('cost_materials').select('provider_id, group_id'),
                supabase.from('supply_groups').select('id, provider_id'),
            ]);
            const gProv = new Map((grs.data || []).map((g: any) => [g.id, g.provider_id]));
            const counts: Record<number, number> = {};
            for (const m of (mats.data || []) as any[]) {
                const pid = m.group_id ? gProv.get(m.group_id) : m.provider_id;
                if (pid) counts[pid] = (counts[pid] || 0) + 1;
            }
            setMatCount(counts);
        } catch (error: any) {
            setMsg('No se pudieron cargar los proveedores: ' + (error?.message || error));
        } finally {
            setLoading(false);
        }
    };

    const deleteProvider = async (p: Provider) => {
        if (!window.confirm(`¿Borrar "${p.name || 'este proveedor'}"? No se puede deshacer.`)) return;
        const { error } = await supabase.from('providers').delete().eq('id', p.id);
        if (error) {
            setMsg(error.code === '23503' // Foreign key violation
                ? 'No se puede borrar: está asignado a uno o más insumos.'
                : 'No se pudo borrar: ' + error.message);
            return;
        }
        setProviders(ps => ps.filter(x => x.id !== p.id));
    };

    const isFlag = (p: Provider, f: Flag) =>
        f === 'sin_whatsapp' ? !String(p.whatsapp || '').trim() : !(matCount[p.id] > 0);
    const counts = useMemo(() => ({
        sin_whatsapp: providers.filter(p => isFlag(p, 'sin_whatsapp')).length,
        sin_insumos: providers.filter(p => isFlag(p, 'sin_insumos')).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [providers, matCount]);
    const rows = useMemo(() => (fFlag ? providers.filter(p => isFlag(p, fFlag)) : providers),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [providers, matCount, fFlag]);
    const conInsumos = providers.length - counts.sin_insumos;

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            {/* ── Encabezado ─────────────────────────────────────────────────── */}
            <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Proveedores</h1>
                    <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                        Contacto de cada proveedor. Con el WhatsApp cargado, el pedido de insumos se le manda en un click. Lo que cambies se guarda solo.
                    </p>
                </div>
                <Button onClick={createProvider}><Plus size={16} /> Nuevo proveedor</Button>
            </header>

            {msg && (
                <div role="alert" className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--danger)_30%,var(--border))] bg-[color-mix(in_oklab,var(--danger)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--danger)]">
                    <AlertCircle size={16} className="shrink-0" />
                    <span className="min-w-0 flex-1">{msg}</span>
                    <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setMsg(null)}><X size={14} /></Button>
                </div>
            )}

            {/* ── Resumen ────────────────────────────────────────────────────── */}
            <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MetricCard label="Proveedores" value={providers.length} locale="es-AR"
                    context={`${conInsumos} con insumos asignados`} />
                <MetricCard label="Sin WhatsApp" value={counts.sin_whatsapp} locale="es-AR"
                    context="No se les puede mandar el pedido directo" />
                <MetricCard label="Sin insumos" value={counts.sin_insumos} locale="es-AR"
                    context="No aparecen al armar un pedido" />
                <MetricCard label="Insumos cubiertos" value={Object.values(matCount).reduce((a, b) => a + b, 0)} locale="es-AR"
                    context="Variantes con proveedor asignado" />
            </section>

            {/* Grilla editable: nombre, WhatsApp y mail se editan acá mismo y se autoguardan. */}
            <DataGrid
                rows={rows}
                loading={loading}
                pageSize={30}
                searchPlaceholder="Buscar por nombre, teléfono o mail…"
                emptyTitle="Todavía no hay proveedores"
                emptyText="Cargá el primero para poder asignarle insumos y pedirle mercadería."
                emptyAction={<Button size="sm" onClick={createProvider}><Plus size={14} /> Nuevo proveedor</Button>}
                filtersActive={fFlag != null}
                onClearFilters={() => setFFlag(null)}
                toolbar={
                    <ChipGroup label="Filtros rápidos" multiple={false}
                        value={fFlag ? [fFlag] : []}
                        onValueChange={v => setFFlag((v[0] as Flag) ?? null)}
                        options={[
                            { value: 'sin_whatsapp', label: `Sin WhatsApp · ${counts.sin_whatsapp}` },
                            { value: 'sin_insumos', label: `Sin insumos · ${counts.sin_insumos}` },
                        ]} />
                }
                cols={[
                    {
                        key: 'name', header: 'Nombre o empresa', sort: p => p.name || '',
                        render: p => <TextCell value={p.name} placeholder="Sin nombre" className="min-w-[12rem] font-medium" onChange={v => edit(p.id, { name: v })} />,
                    },
                    {
                        key: 'whatsapp', header: 'WhatsApp', sort: p => p.whatsapp || '',
                        render: p => (
                            <span className="flex items-center gap-1">
                                {/* Placeholder que NO parece un dato: antes "11 2345-6789" se leía como un teléfono real. */}
                                <TextCell value={p.whatsapp} placeholder="Agregar WhatsApp" className="min-w-[9rem] tabular-nums" onChange={v => edit(p.id, { whatsapp: v })} />
                                {p.whatsapp && (
                                    <a href={`https://wa.me/${String(p.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                                        title="Abrir chat de WhatsApp" aria-label="Abrir chat de WhatsApp"
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-[var(--radius-control)] text-faint transition-colors hover:bg-muted hover:text-[var(--success)]">
                                        <Phone size={14} />
                                    </a>
                                )}
                            </span>
                        ),
                    },
                    {
                        key: 'email', header: 'Mail', sort: p => p.email || '', hideOnMobile: true,
                        render: p => (
                            <span className="flex items-center gap-1">
                                <TextCell value={p.email} placeholder="Agregar mail" className="min-w-[11rem]" onChange={v => edit(p.id, { email: v })} />
                                {p.email && (
                                    <a href={`mailto:${p.email}`} title="Mandar mail" aria-label="Mandar mail"
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-[var(--radius-control)] text-faint transition-colors hover:bg-muted hover:text-ink">
                                        <Mail size={14} />
                                    </a>
                                )}
                            </span>
                        ),
                    },
                    {
                        // Idioma del PDF de pedido (ej. Christopher/Alibaba recibe en inglés con name_en).
                        key: 'lang', header: 'Pedido en', width: 'w-32', hideOnMobile: true, sort: p => p.order_lang || 'es',
                        render: p => (
                            <SelectCell value={p.order_lang || 'es'} onChange={v => editNow(p.id, { order_lang: v })}
                                options={[{ value: 'es', label: 'Español' }, { value: 'en', label: 'Inglés' }]} />
                        ),
                    },
                    {
                        key: 'insumos', header: 'Insumos', align: 'right', width: 'w-24', hideOnMobile: true,
                        sort: p => matCount[p.id] || 0,
                        render: p => (
                            <span className={(matCount[p.id] || 0) === 0 ? 'text-faint' : 'font-medium text-ink'}>
                                {matCount[p.id] || 0}
                            </span>
                        ),
                    },
                    {
                        key: 'acciones', header: '', width: 'w-32', align: 'right',
                        render: p => (
                            <div className="flex items-center justify-end gap-1">
                                <SaveState state={state[String(p.id)]} />
                                {/* El borrado sí confirma: es irreversible. */}
                                <Button variant="ghost" size="sm" aria-label={`Borrar ${p.name || 'proveedor'}`} title="Borrar proveedor"
                                    className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                                    onClick={() => deleteProvider(p)}><Trash2 size={14} /></Button>
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}
